import { readonly, ref, useContext, computed } from '@nuxtjs/composition-api';
import { useVSFContext } from '@vue-storefront/core';
import { Logger } from '~/helpers/logger';
import { placeOrderCommand } from '~/modules/checkout/composables/useMakeOrder/commands/placeOrderCommand';
import useCart from '~/modules/checkout/composables/useCart';
import type { PlaceOrderOutput } from '~/modules/GraphQL/types';
import type { UseMakeOrderErrors, UseMakeOrderInterface } from './useMakeOrder';
import {ComposableFunctionArgs, useErrorLog, useUserZenni} from '~/composables';
import sharedRef from '~/helpers/sharedRef';
import {useCartStore} from '~/modules/checkout/stores/cart';

/**
 * Allows making an order from a cart.
 *
 * See the {@link UseMakeOrderInterface} for a list of methods and values available in this composable.
 */
export function useMakeOrder(): UseMakeOrderInterface {
  const loading = ref(false);
  const error = ref<UseMakeOrderErrors>({ make: null });
  const { cart } = useCart();
  const cartStore = useCartStore();
  const context = useContext();
  const {$sharedRefsMap} = useVSFContext();
  const makeOrderError = sharedRef(error, 'useMakeOrder-error', $sharedRefsMap);
  const { logError } = useErrorLog({cartStore});
  const make = async (params?: ComposableFunctionArgs<unknown>): Promise<PlaceOrderOutput | null> => {
    Logger.debug('useMakeOrder.make');
    let placedOrder = null;
    try {
      loading.value = true;
      placedOrder = await placeOrderCommand.execute(context, cart.value.id, params?.customQuery ?? null);
      makeOrderError.value.make = null;
    } catch (err) {
      makeOrderError.value.make = err;
      logError({err, errorType: 'makeOrder'});
      Logger.error('useMakeOrder.make', err);
    } finally {
      loading.value = false;
    }

    return placedOrder;
  };

  return {
    make,
    error: computed(() => makeOrderError.value),
    loading: readonly(loading),
  };
}

export * from './useMakeOrder';
export default useMakeOrder;
